<template>
  <div class="min-h-screen" data-theme="teal">
    <NavBar :signed-in="signedIn" :user-name="userName" active-page="coaches" />
    <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <a :href="'/coaches'" class="text-sm text-blue-700 hover:text-blue-900 mb-6 inline-block">&larr; {{ t('coaches.back_to_list') }}</a>

      <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div v-if="coach.logo_url" class="mb-4">
          <img :src="coach.logo_url" class="w-20 h-20 rounded-lg object-cover border border-gray-200" />
        </div>
        <h1 class="text-2xl font-bold text-gray-900 mb-2">{{ coach.name }}</h1>
        <div class="flex flex-wrap gap-2 mb-4">
          <span v-if="coach.city" class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">{{ coach.city }}</span>
          <span v-if="coach.club" class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">{{ coach.club }}</span>
          <span v-if="coach.online" class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-600 text-white">{{ t('coaches.online') }}</span>
        </div>

        <div v-if="coach.pending && userAdmin" class="mb-4">
          <span class="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800 mr-2">{{ t('coaches.pending') }}</span>
          <button @click="approveItem" class="px-4 py-1.5 text-sm font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition">{{ t('coaches.approve') }}</button>
        </div>
        <div v-else-if="coach.pending" class="mb-4">
          <span class="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">{{ t('coaches.pending') }}</span>
        </div>

        <p v-if="coachDescription" class="text-gray-600 mb-4 whitespace-pre-line">{{ coachDescription }}</p>
        <div class="flex items-center gap-3 mb-6">
          <a v-if="coach.phone" :href="'tel:' + coach.phone"
             class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-700 rounded-lg hover:bg-blue-800 transition">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
            {{ coach.phone }}
          </a>
          <a v-if="coach.facebook_link" :href="coach.facebook_link" target="_blank" class="hover:opacity-80 transition">
            <svg class="w-7 h-7" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
          </a>
          <a v-if="coach.instagram_link" :href="coach.instagram_link" target="_blank" class="hover:opacity-80 transition">
            <svg class="w-7 h-7" viewBox="0 0 24 24"><defs><linearGradient id="ig-show" x1="0%" y1="100%" x2="100%" y2="0%"><stop offset="0%" stop-color="#feda75"/><stop offset="25%" stop-color="#fa7e1e"/><stop offset="50%" stop-color="#d62976"/><stop offset="75%" stop-color="#962fbf"/><stop offset="100%" stop-color="#4f5bd5"/></linearGradient></defs><path fill="url(#ig-show)" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12s.014 3.668.072 4.948c.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24s3.668-.014 4.948-.072c4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948s-.014-3.667-.072-4.947c-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
          </a>
        </div>

        <!-- Locations -->
        <div v-if="locationsWithCoords.length > 0" class="mb-6">
          <h3 v-if="locationsWithCoords.length > 1" class="text-sm font-semibold text-gray-700 mb-2">{{ t('coaches.locations') }} ({{ locationsWithCoords.length }})</h3>
          <div v-if="locationsWithCoords.length > 1" class="flex flex-wrap gap-2 mb-3">
            <span v-for="loc in locationsWithCoords" :key="loc.id" class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
              {{ loc.name || t('coaches.location') }}
            </span>
          </div>
          <div class="rounded-lg border border-gray-200 overflow-hidden map-container">
            <div ref="mapContainer" style="height: 300px; width: 100%;"></div>
          </div>
        </div>

        <div v-if="userAdmin || coach.user_id === userId" class="flex gap-3 mt-4">
          <a :href="`/coaches?edit=${coach.id}`" class="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition">{{ t('coaches.edit') }}</a>
          <button @click="deleteItem" class="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition">{{ t('coaches.delete') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import NavBar from './NavBar.vue'
import L from 'leaflet'
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({ iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png', iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png', shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png' })

const props = defineProps({ signedIn: Boolean, userName: String, userId: Number, userAdmin: Boolean, coach: Object, csrfToken: String })
const { t, locale } = useI18n()
const coach = reactive({ ...props.coach })
const mapContainer = ref(null)

const coachDescription = computed(() => {
  return locale.value === 'ru' ? (coach.description_ru || coach.description_ro) : (coach.description_ro || coach.description_ru)
})

const locationsWithCoords = computed(() => (coach.locations || []).filter(l => l.latitude && l.longitude))

async function approveItem() {
  try {
    const res = await fetch(`/coaches/${coach.id}/approve`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': props.csrfToken, 'Accept': 'application/json' }
    })
    if (res.ok) coach.pending = false
  } catch {}
}

async function deleteItem() {
  if (!confirm(t('coaches.confirm_delete_message'))) return
  try {
    const res = await fetch(`/coaches/${coach.id}`, {
      method: 'DELETE', headers: { 'X-CSRF-Token': props.csrfToken, 'Accept': 'application/json' }
    })
    if (res.ok) window.location.href = '/coaches'
  } catch {}
}

onMounted(() => {
  if (locationsWithCoords.value.length > 0 && mapContainer.value) {
    const first = locationsWithCoords.value[0]
    const map = L.map(mapContainer.value).setView([parseFloat(first.latitude), parseFloat(first.longitude)], 13)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '&copy; OpenStreetMap' }).addTo(map)

    const markers = []
    locationsWithCoords.value.forEach(loc => {
      const marker = L.marker([parseFloat(loc.latitude), parseFloat(loc.longitude)]).addTo(map)
      if (loc.name) marker.bindPopup(`<strong>${loc.name}</strong>`)
      markers.push(marker)
    })

    if (markers.length > 1) map.fitBounds(L.featureGroup(markers).getBounds().pad(0.2))
    setTimeout(() => map.invalidateSize(), 100)
  }
})
</script>
